import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Grid, Card, CardContent, Tabs, Tab, Switch, FormControlLabel, Select,
  InputLabel, FormControl,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HistoryIcon from '@mui/icons-material/History';
import {
  ALL_EMAIL_TEMPLATES_QUERY,
  CREATE_EMAIL_TEMPLATE_MUTATION,
  UPDATE_EMAIL_TEMPLATE_MUTATION,
  DELETE_EMAIL_TEMPLATE_MUTATION,
  ALL_NOTIFICATION_TRIGGERS_QUERY,
  CREATE_NOTIFICATION_TRIGGER_MUTATION,
  UPDATE_NOTIFICATION_TRIGGER_MUTATION,
  DELETE_NOTIFICATION_TRIGGER_MUTATION,
  EMAIL_LOGS_QUERY,
} from '../graphql/queries';

const EVENT_TYPES = [
  { value: 'user_registered', label: 'User Registered' },
  { value: 'interest_sent', label: 'Interest Sent' },
  { value: 'interest_accepted', label: 'Interest Accepted' },
  { value: 'meeting_created', label: 'Meeting Created' },
  { value: 'contract_created', label: 'Contract Created' },
  { value: 'payment_due', label: 'Payment Due' },
  { value: 'ceremony_upcoming', label: 'Ceremony Upcoming' },
  { value: 'password_reset', label: 'Password Reset' },
  { value: 'booking_confirmed', label: 'Booking Confirmed' },
  { value: 'vendor_assigned', label: 'Vendor Assigned' },
];

const RECIPIENT_TYPES = [
  { value: 'user', label: 'User' },
  { value: 'sender', label: 'Sender' },
  { value: 'recipient', label: 'Recipient' },
  { value: 'participants', label: 'Participants' },
  { value: 'all', label: 'All Users' },
];

const EMPTY_TEMPLATE = { name: '', subject: '', body: '', description: '', variables: '', isActive: true };
const EMPTY_TRIGGER = { name: '', eventType: '', templateId: '', description: '', isActive: true, sendEmail: true, sendInApp: true, recipientType: 'user' };

export default function AdminEmailTemplatesPage() {
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', ok: true });

  // Template state
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);

  // Trigger state
  const [triggerDialog, setTriggerDialog] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [triggerForm, setTriggerForm] = useState(EMPTY_TRIGGER);

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  // Templates
  const { data: templatesData, loading: templatesLoading, refetch: refetchTemplates } = useQuery(ALL_EMAIL_TEMPLATES_QUERY, { fetchPolicy: 'cache-and-network' });
  const [createTemplate] = useMutation(CREATE_EMAIL_TEMPLATE_MUTATION, {
    onCompleted: () => { setTemplateDialog(false); refetchTemplates(); toast('Template created'); },
    onError: (e) => toast(e.message, false),
  });
  const [updateTemplate] = useMutation(UPDATE_EMAIL_TEMPLATE_MUTATION, {
    onCompleted: () => { setTemplateDialog(false); refetchTemplates(); toast('Template updated'); },
    onError: (e) => toast(e.message, false),
  });
  const [deleteTemplate] = useMutation(DELETE_EMAIL_TEMPLATE_MUTATION, {
    onCompleted: () => { refetchTemplates(); toast('Template deleted'); },
    onError: (e) => toast(e.message, false),
  });

  // Triggers
  const { data: triggersData, loading: triggersLoading, refetch: refetchTriggers } = useQuery(ALL_NOTIFICATION_TRIGGERS_QUERY, { fetchPolicy: 'cache-and-network' });
  const [createTrigger] = useMutation(CREATE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { setTriggerDialog(false); refetchTriggers(); toast('Trigger created'); },
    onError: (e) => toast(e.message, false),
  });
  const [updateTrigger] = useMutation(UPDATE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { setTriggerDialog(false); refetchTriggers(); toast('Trigger updated'); },
    onError: (e) => toast(e.message, false),
  });
  const [deleteTrigger] = useMutation(DELETE_NOTIFICATION_TRIGGER_MUTATION, {
    onCompleted: () => { refetchTriggers(); toast('Trigger deleted'); },
    onError: (e) => toast(e.message, false),
  });

  // Logs
  const { data: logsData, loading: logsLoading } = useQuery(EMAIL_LOGS_QUERY, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
  });

  const templates = templatesData?.allEmailTemplates || [];
  const triggers = triggersData?.allNotificationTriggers || [];
  const logs = logsData?.emailLogs || [];

  // Template handlers
  const openNewTemplate = () => { setEditingTemplate(null); setTemplateForm(EMPTY_TEMPLATE); setTemplateDialog(true); };
  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, subject: t.subject, body: t.body, description: t.description || '', variables: (t.variables || []).join(', '), isActive: t.isActive });
    setTemplateDialog(true);
  };
  const handleSaveTemplate = () => {
    const vars = templateForm.variables ? templateForm.variables.split(',').map(v => v.trim()).filter(Boolean) : [];
    if (editingTemplate) {
      updateTemplate({ variables: { id: editingTemplate.id, ...templateForm, variables: vars } });
    } else {
      createTemplate({ variables: { ...templateForm, variables: vars } });
    }
  };

  // Trigger handlers
  const openNewTrigger = () => { setEditingTrigger(null); setTriggerForm(EMPTY_TRIGGER); setTriggerDialog(true); };
  const openEditTrigger = (t) => {
    setEditingTrigger(t);
    setTriggerForm({ name: t.name, eventType: t.eventType, templateId: t.templateId || '', description: t.description || '', isActive: t.isActive, sendEmail: t.sendEmail, sendInApp: t.sendInApp, recipientType: t.recipientType });
    setTriggerDialog(true);
  };
  const handleSaveTrigger = () => {
    const data = { ...triggerForm, templateId: triggerForm.templateId || null };
    if (editingTrigger) {
      updateTrigger({ variables: { id: editingTrigger.id, ...data } });
    } else {
      createTrigger({ variables: data });
    }
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Email Templates & Triggers</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email templates, notification triggers, and view email logs
          </Typography>
        </Box>
      </Stack>

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<EmailIcon />} label="Email Templates" />
        <Tab icon={<NotificationsIcon />} label="Notification Triggers" />
        <Tab icon={<HistoryIcon />} label="Email Logs" />
      </Tabs>

      {/* ─── Email Templates Tab ─── */}
      {tab === 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Email Templates ({templates.length})</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTemplate}>New Template</Button>
            </Stack>
            {templatesLoading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Variables</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t.id}>
                      <TableCell><strong>{t.name}</strong></TableCell>
                      <TableCell>{t.subject}</TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell>{(t.variables || []).map(v => <Chip key={v} label={`{{${v}}}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)}</TableCell>
                      <TableCell><Chip label={t.isActive ? 'Active' : 'Inactive'} color={t.isActive ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditTemplate(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { if (window.confirm('Delete this template?')) deleteTemplate({ variables: { id: t.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Notification Triggers Tab ─── */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Notification Triggers ({triggers.length})</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTrigger}>New Trigger</Button>
            </Stack>
            {triggersLoading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>In-App</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {triggers.map(t => (
                    <TableRow key={t.id}>
                      <TableCell><strong>{t.name}</strong></TableCell>
                      <TableCell><Chip label={t.eventType} size="small" variant="outlined" /></TableCell>
                      <TableCell>{t.template?.name || '—'}</TableCell>
                      <TableCell><Chip label={t.sendEmail ? 'Yes' : 'No'} color={t.sendEmail ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell><Chip label={t.sendInApp ? 'Yes' : 'No'} color={t.sendInApp ? 'info' : 'default'} size="small" /></TableCell>
                      <TableCell>{t.recipientType}</TableCell>
                      <TableCell><Chip label={t.isActive ? 'Active' : 'Inactive'} color={t.isActive ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditTrigger(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { if (window.confirm('Delete this trigger?')) deleteTrigger({ variables: { id: t.id } }); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Email Logs Tab ─── */}
      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Email Logs (Last 50)</Typography>
            {logsLoading ? <CircularProgress /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(l => (
                    <TableRow key={l.id}>
                      <TableCell>{l.sentAt ? new Date(l.sentAt).toLocaleString() : new Date(l.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{l.recipientEmail}</TableCell>
                      <TableCell>{l.subject}</TableCell>
                      <TableCell>{l.template?.name || '—'}</TableCell>
                      <TableCell>{l.trigger?.name || '—'}</TableCell>
                      <TableCell><Chip label={l.status} color={l.status === 'sent' ? 'success' : 'error'} size="small" /></TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.errorMessage || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && <TableRow><TableCell colSpan={7} align="center">No email logs yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Template Dialog ─── */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Email Template' : 'Create Email Template'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Template Name *" value={templateForm.name} onChange={(e) => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. welcome" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Subject *" value={templateForm.subject} onChange={(e) => setTemplateForm(f => ({ ...f, subject: e.target.value }))} placeholder="Welcome to Matrimony!" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={templateForm.description} onChange={(e) => setTemplateForm(f => ({ ...f, description: e.target.value }))} placeholder="When is this template used?" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Variables (comma-separated)" value={templateForm.variables} onChange={(e) => setTemplateForm(f => ({ ...f, variables: e.target.value }))} placeholder="firstName, email, link" helperText="Use {{variableName}} in subject/body" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={10} label="Body (HTML) *" value={templateForm.body} onChange={(e) => setTemplateForm(f => ({ ...f, body: e.target.value }))} placeholder="<h1>Hello {{firstName}}</h1>" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={templateForm.isActive} onChange={(e) => setTemplateForm(f => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>{editingTemplate ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Trigger Dialog ─── */}
      <Dialog open={triggerDialog} onClose={() => setTriggerDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTrigger ? 'Edit Notification Trigger' : 'Create Notification Trigger'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Trigger Name *" value={triggerForm.name} onChange={(e) => setTriggerForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. New User Welcome" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type *</InputLabel>
                <Select value={triggerForm.eventType} label="Event Type *" onChange={(e) => setTriggerForm(f => ({ ...f, eventType: e.target.value }))}>
                  {EVENT_TYPES.map(et => <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Email Template</InputLabel>
                <Select value={triggerForm.templateId} label="Email Template" onChange={(e) => setTriggerForm(f => ({ ...f, templateId: e.target.value }))}>
                  <MenuItem value="">None</MenuItem>
                  {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name} — {t.subject}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Recipient Type</InputLabel>
                <Select value={triggerForm.recipientType} label="Recipient Type" onChange={(e) => setTriggerForm(f => ({ ...f, recipientType: e.target.value }))}>
                  {RECIPIENT_TYPES.map(rt => <MenuItem key={rt.value} value={rt.value}>{rt.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={triggerForm.description} onChange={(e) => setTriggerForm(f => ({ ...f, description: e.target.value }))} placeholder="When is this trigger fired?" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={triggerForm.sendEmail} onChange={(e) => setTriggerForm(f => ({ ...f, sendEmail: e.target.checked }))} />} label="Send Email" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={triggerForm.sendInApp} onChange={(e) => setTriggerForm(f => ({ ...f, sendInApp: e.target.checked }))} />} label="Send In-App Notification" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={triggerForm.isActive} onChange={(e) => setTriggerForm(f => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTrigger}>{editingTrigger ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
